import type { CompositeWeights, Conf, Div, NonEmptyArray } from "./types.ts";
import type { RatingKey } from "./types.basketball.ts";

export const COMPOSITE_WEIGHTS: CompositeWeights<RatingKey> = {
	pace: {
		ratings: ["spd", "jmp", "dnk", "tp", "drb", "pss"],
	},
	usage: {
		ratings: ["ins", "dnk", "fg", "tp", "spd", "hgt", "drb", "oiq"],
		weights: [1.5, 1, 1, 1, 0.5, 0.5, 0.5, 0.5],
		skill: {
			label: "V",
			cutoff: 0.61,
		},
	},
	dribbling: {
		ratings: ["drb", "spd"],
		weights: [1, 1],
		skill: {
			label: "B",
			cutoff: 0.68,
		},
	},
	passing: {
		ratings: ["drb", "pss", "oiq"],
		weights: [0.4, 1, 0.5],
		skill: {
			label: "Ps",
			cutoff: 0.63,
		},
	},
	turnovers: {
		ratings: [50, "ins", "pss", "oiq"],
		weights: [0.5, 1, 1, -1],
	},
	shootingAtRim: {
		ratings: ["hgt", "stre", "dnk", "oiq"],
		weights: [2, 0.3, 0.3, 0.2],
	},
	shootingLowPost: {
		ratings: ["hgt", "stre", "spd", "ins", "oiq"],
		weights: [1, 0.6, 0.2, 1, 0.4],
		skill: {
			label: "Po",
			cutoff: 0.61,
		},
	},
	shootingMidRange: {
		ratings: ["oiq", "fg", "stre"],
		weights: [-0.5, 1, 0.2],
	},
	shootingThreePointer: {
		ratings: ["oiq", "tp"],
		weights: [0.1, 1],
		skill: {
			label: "3",
			cutoff: 0.59,
		},
	},
	shootingFT: {
		ratings: ["ft"],
	},
	rebounding: {
		ratings: ["hgt", "stre", "jmp", "reb", "oiq", "diq"],
		weights: [2, 0.1, 0.1, 2, 0.5, 0.5],
		skill: {
			label: "R",
			cutoff: 0.61,
		},
	},
	stealing: {
		ratings: [50, "spd", "diq"],
		weights: [1, 1, 2],
	},
	blocking: {
		ratings: ["hgt", "jmp", "diq"],
		weights: [2.5, 1.5, 0.5],
	},
	fouling: {
		ratings: [50, "hgt", "diq", "spd"],
		weights: [3, 1, -1, -1],
	},
	drawingFouls: {
		ratings: ["hgt", "spd", "drb", "dnk", "oiq"],
		weights: [1, 1, 1, 1, 1],
	},
	defense: {
		ratings: ["hgt", "stre", "spd", "jmp", "diq"],
		weights: [1, 1, 1, 0.5, 2],
	},
	defenseInterior: {
		ratings: ["hgt", "stre", "spd", "jmp", "diq"],
		weights: [2.5, 1, 0.5, 0.5, 2],
		skill: {
			label: "Di",
			cutoff: 0.57,
		},
	},
	defensePerimeter: {
		ratings: ["hgt", "stre", "spd", "jmp", "diq"],
		weights: [0.5, 0.5, 2, 0.5, 1],
		skill: {
			label: "Dp",
			cutoff: 0.61,
		},
	},
	endurance: {
		ratings: [50, "endu"],
		weights: [1, 1],
	},
	athleticism: {
		ratings: ["stre", "spd", "jmp", "hgt"],
		weights: [1, 1, 1, 0.75],
		skill: {
			label: "A",
			cutoff: 0.63,
		},
	},
	jumpBall: {
		ratings: ["hgt", "jmp"],
		weights: [1, 0.25],
	},
};

export const PLAYER_GAME_STATS = {
	all: {
		name: "All",
		stats: [
			"gs",
			"min",
			"fg",
			"fga",
			"fgp",
			"tp",
			"tpa",
			"tpp",
			"ft",
			"fta",
			"ftp",
			"orb",
			"drb",
			"trb",
			"ast",
			"tov",
			"stl",
			"blk",
			"ba",
			"pf",
			"pts",
			"pm",
			"gmsc",
		],
		sortBy: ["min"],
	},
};

export const PLAYER_SUMMARY = {
	summary: {
		name: "Summary",
		stats: [
			"gp",
			"min",
			"pts",
			"trb",
			"ast",
			"fgp",
			"tpp",
			"ftp",
			"tsp",
			"per",
			"ws",
		],
	},
};

export const PLAYER_STATS_TABLES = {
	regular: {
		name: "Per Game",
		stats: [
			"gp",
			"gs",
			"min",
			"fg",
			"fga",
			"fgp",
			"tp",
			"tpa",
			"tpp",
			"2p",
			"2pa",
			"2pp",
			"efg",
			"ft",
			"fta",
			"ftp",
			"orb",
			"drb",
			"trb",
			"ast",
			"tov",
			"stl",
			"blk",
			"ba",
			"pf",
			"pts",
		],
	},
	shotLocations: {
		name: "Shot Locations and Feats",
		stats: [
			"gp",
			"gs",
			"min",
			"fgAtRim",
			"fgaAtRim",
			"fgpAtRim",
			"fgLowPost",
			"fgaLowPost",
			"fgpLowPost",
			"fgMidRange",
			"fgaMidRange",
			"fgpMidRange",
			"tp",
			"tpa",
			"tpp",
			"dd",
			"td",
			"qd",
			"fxf",
		],
		superCols: [
			{
				title: "",
				colspan: 7,
			},
			{
				title: "At Rim",
				colspan: 3,
			},
			{
				title: "Low Post",
				colspan: 3,
			},
			{
				title: "Mid-Range",
				colspan: 3,
			},
			{
				title: "3PT",
				desc: "Three-Pointers",
				colspan: 3,
			},
			{
				title: "Feats",
				desc: "Statistical Feats",
				colspan: 4,
			},
		],
	},
	advanced: {
		name: "Advanced",
		stats: [
			"gp",
			"gs",
			"min",
			"per",
			"ewa",
			"tsp",
			"tpar",
			"ftr",
			"orbp",
			"drbp",
			"trbp",
			"astp",
			"stlp",
			"blkp",
			"tovp",
			"usgp",
			//"pm",
			"pm100",
			"onOff100",
			"ortg",
			"drtg",
			"ows",
			"dws",
			"ws",
			"ws48",
			"obpm",
			"dbpm",
			"bpm",
			"vorp",
		],
	},
	gameHighs: {
		name: "Game Highs",
		stats: [
			"gp",
			"minMax",
			"fgMax",
			"fgaMax",
			"tpMax",
			"tpaMax",
			"2pMax",
			"2paMax",
			"ftMax",
			"ftaMax",
			"orbMax",
			"drbMax",
			"trbMax",
			"astMax",
			"tovMax",
			"stlMax",
			"blkMax",
			"baMax",
			"pfMax",
			"ptsMax",
			"pmMax",
			"gmscMax",
		],
	},
};

export const TEAM_STATS_TABLES = {
	team: {
		name: "Team",
		stats: [
			"fg",
			"fga",
			"fgp",
			"tp",
			"tpa",
			"tpp",
			"2p",
			"2pa",
			"2pp",
			"ft",
			"fta",
			"ftp",
			"orb",
			"drb",
			"trb",
			"ast",
			"tov",
			"stl",
			"blk",
			"pf",
			"pts",
			"mov",
		],
	},
	opponent: {
		name: "Opponent",
		stats: [
			"oppFg",
			"oppFga",
			"oppFgp",
			"oppTp",
			"oppTpa",
			"oppTpp",
			"opp2p",
			"opp2pa",
			"opp2pp",
			"oppFt",
			"oppFta",
			"oppFtp",
			"oppOrb",
			"oppDrb",
			"oppTrb",
			"oppAst",
			"oppTov",
			"oppStl",
			"oppBlk",
			"oppPf",
			"oppPts",
			"oppMov",
		],
	},
	teamShotLocations: {
		name: "Shot Locations and Feats",
		stats: [
			"fgAtRim",
			"fgaAtRim",
			"fgpAtRim",
			"fgLowPost",
			"fgaLowPost",
			"fgpLowPost",
			"fgMidRange",
			"fgaMidRange",
			"fgpMidRange",
			"tp",
			"tpa",
			"tpp",
			"dd",
			"td",
			"qd",
			"fxf",
		],
		superCols: [
			{
				title: "",
				colspan: 4,
			},
			{
				title: "At Rim",
				colspan: 3,
			},
			{
				title: "Low Post",
				colspan: 3,
			},
			{
				title: "Mid-Range",
				colspan: 3,
			},
			{
				title: "3PT",
				desc: "Three-Pointers",
				colspan: 3,
			},
			{
				title: "Feats",
				desc: "Statistical Feats",
				colspan: 4,
			},
		],
	},
	opponentShotLocations: {
		name: "Opponent Shot Locations and Feats",
		stats: [
			"oppFgAtRim",
			"oppFgaAtRim",
			"oppFgpAtRim",
			"oppFgLowPost",
			"oppFgaLowPost",
			"oppFgpLowPost",
			"oppFgMidRange",
			"oppFgaMidRange",
			"oppFgpMidRange",
			"oppTp",
			"oppTpa",
			"oppTpp",
			"oppDd",
			"oppTd",
			"oppQd",
			"oppFxf",
		],
		superCols: [
			{
				title: "",
				colspan: 4,
			},
			{
				title: "At Rim",
				colspan: 3,
			},
			{
				title: "Low Post",
				colspan: 3,
			},
			{
				title: "Mid-Range",
				colspan: 3,
			},
			{
				title: "3PT",
				desc: "Three-Pointers",
				colspan: 3,
			},
			{
				title: "Feats",
				desc: "Statistical Feats",
				colspan: 4,
			},
		],
	},
	advanced: {
		name: "Advanced",
		stats: [
			"pw",
			"pl",
			"ortg",
			"drtg",
			"nrtg",
			"pace",
			"tpar",
			"ftr",
			"tsp",
			"efg",
			"tovp",
			"orbp",
			"ftpFga",
			"oppEfg",
			"oppTovp",
			"drbp",
			"oppFtpFga",
		],
		superCols: [
			{
				title: "",
				colspan: 13,
			},
			{
				title: "Offensive Four Factors",
				colspan: 4,
			},
			{
				title: "Defensive Four Factors",
				colspan: 4,
			},
		],
	},
};

export const POSITIONS = ["PG", "G", "SG", "GF", "SF", "F", "PF", "FC", "C"];

export const POSITION_COUNTS = {};

export const RATINGS: RatingKey[] = [
	"hgt",
	"stre",
	"spd",
	"jmp",
	"endu",
	"ins",
	"dnk",
	"ft",
	"fg",
	"tp",
	"oiq",
	"diq",
	"drb",
	"pss",
	"reb",
];

export const DEFAULT_CONFS: NonEmptyArray<Conf> = [
	{
		cid: 0,
		name: "Eastern Conference",
	},
	{
		cid: 1,
		name: "Western Conference",
	},
];

export const DEFAULT_DIVS: NonEmptyArray<Div> = [
	{
		did: 0,
		cid: 0,
		name: "Atlantic",
	},
	{
		did: 1,
		cid: 0,
		name: "Central",
	},
	{
		did: 2,
		cid: 0,
		name: "Southeast",
	},
	{
		did: 3,
		cid: 1,
		name: "Southwest",
	},
	{
		did: 4,
		cid: 1,
		name: "Northwest",
	},
	{
		did: 5,
		cid: 1,
		name: "Pacific",
	},
];
